// ─────────────────────────────────────────────────────────────────────────
// Por vincular — facturas de venta emitidas fuera del ERP (Enternet) que el SII ya
// informó y que todavía no están ligadas a una OT.
//
// Fuente: GET /dte/externas-por-vincular?periodo=YYYY-MM (sin periodo = últimos 90 días;
// ?desde=todo = todo el histórico de Enternet sin OT, tope 500).
// "Vincular a OT" abre FacturarModal en modo `externa`; el backend de vinculación
// exige admin u operator, por eso el botón solo se muestra a esos roles.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import FacturarModal from "../../components/design-system/FacturarModal";
import { S, fmt, fmtD, labelPeriodo, ultimosPeriodos, estiloEstado } from "./ui";

const Badge = ({ s }) => (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.border}`, whiteSpace: "nowrap" }}>{s.label}</span>
);

const PorVincular = ({ roles = [], onAmbiente }) => {
  const isMobile = useIsMobile();
  const puedeVincular = roles.some(r => ["admin", "operator"].includes(r));
  const [periodo, setPeriodo] = useState("");   // "" = últimos 90 días (por defecto) · "todo" = histórico completo
  const [facturas, setFacturas] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  const periodos = useMemo(() => ultimosPeriodos(12), []);

  // Una respuesta lenta de un periodo anterior no pisa la del periodo actual.
  const pedidoRef = useRef(0);
  const cargar = useCallback(async () => {
    const n = ++pedidoRef.current;
    setCargando(true); setError("");
    try {
      const params = periodo === "todo" ? { desde: "todo" } : periodo ? { periodo } : {};
      const { data } = await backend.get("dte/externas-por-vincular", { params });
      if (n === pedidoRef.current) {
        setFacturas(data.facturas || []);
        if (data.ambiente) onAmbiente?.(data.ambiente === "produccion");
      }
    } catch (e) { if (n === pedidoRef.current) setError(e?.response?.data?.error || e.message); }
    if (n === pedidoRef.current) setCargando(false);
  }, [periodo, onAmbiente]);
  useEffect(() => { cargar(); }, [cargar]);

  const boton = f => puedeVincular && (
    <button style={S.btn("#2ecc71", true)} onClick={e => { e.stopPropagation(); setTarget({ factura: f }); }}>Vincular a OT</button>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 12, color: "#9aa0a6", lineHeight: 1.5 }}>
        Facturas emitidas fuera del ERP (Enternet) que el SII ya informó y que todavía no están ligadas a una OT.
        Al vincularlas, las guías entregadas quedan facturadas.
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{ ...S.input, flex: isMobile ? "1 1 140px" : undefined }}>
          <option value="">Últimos 90 días</option>
          <option value="todo">Todo el histórico (tope 500)</option>
          {periodos.map(p => <option key={p} value={p}>{labelPeriodo(p)}</option>)}
        </select>
        {facturas && <span style={{ fontSize: 11, color: "#8a8a8a" }}>{facturas.length} {facturas.length === 1 ? "factura" : "facturas"}</span>}
      </div>

      {error && <div style={{ color: "#e74c3c", fontSize: 12 }}>{error}</div>}

      {facturas === null ? (
        <div style={{ color: "#8a8a8a", fontSize: 12 }}>{cargando ? "Cargando…" : ""}</div>
      ) : facturas.length === 0 ? (
        <div style={{ ...S.card, color: "#8a8a8a", fontSize: 12 }}>No hay facturas externas por vincular.</div>
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {facturas.map(f => (
            <div key={f._id} style={{ ...S.card, padding: "10px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <span style={S.pill("#2ecc71")}>F {f.folio}</span>
                <span style={{ color: "#fff", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmt(f.montoTotal)}</span>
              </div>
              <div style={{ color: "#fff", fontWeight: 700, marginTop: 6, fontSize: 13 }}>{f.razonReceptor || f.clienteNombre || f.rutReceptor}</div>
              <div style={{ fontSize: 10, color: "#8a8a8a", fontFamily: "monospace" }}>{f.rutReceptor}</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6, flexWrap: "wrap", fontSize: 11, color: "#8a8a8a" }}>
                <span>{fmtD(f.fecha)}</span>
                <Badge s={estiloEstado(f.estado)} />
              </div>
              {puedeVincular && <div style={{ marginTop: 8 }}>{boton(f)}</div>}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={S.th}>Folio</th><th style={S.th}>Fecha</th><th style={S.th}>Cliente</th>
                <th style={{ ...S.th, textAlign: "right" }}>Total</th><th style={S.th}>Estado SII</th><th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {facturas.map(f => (
                <tr key={f._id}>
                  <td style={S.td}><span style={S.pill("#2ecc71")}>F {f.folio}</span></td>
                  <td style={S.td}>{fmtD(f.fecha)}</td>
                  <td style={{ ...S.td, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                    <div style={{ color: "#fff" }}>{f.razonReceptor || f.clienteNombre || "—"}</div>
                    <div style={{ fontSize: 10, color: "#8a8a8a", fontFamily: "monospace" }}>{f.rutReceptor}</div>
                  </td>
                  <td style={{ ...S.td, ...S.num, color: "#fff", fontWeight: 700 }}>{fmt(f.montoTotal)}</td>
                  <td style={S.td}><Badge s={estiloEstado(f.estado)} /></td>
                  <td style={S.td}>{boton(f)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {target && puedeVincular && (
        <FacturarModal fuente="externa" target={target} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); cargar(); }} />
      )}
    </div>
  );
};

export default PorVincular;
